import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogOverlay,
  DialogTitle,
  TextField,
  cn,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { LucideX } from 'lucide-react';

import type { ModulePermissions } from '../../_generated/permissions/types';

import { PermissionsEditor } from './PermissionsEditor';

export interface AddRoleDialogTranslations {
  title: string;
  fields: {
    name: {
      label: string;
      placeholder: string;
    };
  };
  actions: {
    cancel: string;
    save: string;
  };
  messages: {
    success: string;
    error: string;
  };
}

export interface AddRoleDialogBaseProps {
  isOpen: boolean;
  formData: { name: string; permissions: Record<string, boolean> };
  isFormValid: boolean;
  onUpdateName: (name: string) => void;
  onPermissionChange: (key: string, enabled: boolean) => void;
  onBulkPermissionChange: (changes: Record<string, boolean>) => void;
  onClose: () => void;
  createRole: (params: {
    name: string;
    permissions?: string[];
  }) => Promise<unknown>;
  isCreatePending: boolean;
  permissionModules: ModulePermissions[];
  translations: AddRoleDialogTranslations;
  className?: string;
}

export const AddRoleDialogBase = ({
  isOpen,
  formData,
  isFormValid,
  onUpdateName,
  onPermissionChange,
  onBulkPermissionChange,
  onClose,
  createRole,
  isCreatePending,
  permissionModules,
  translations,
  className,
}: AddRoleDialogBaseProps) => {
  const toast = useToast();

  const handleSave = React.useCallback(async () => {
    if (!isFormValid) return;

    const selectedPermissions = Object.entries(formData.permissions)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);

    try {
      await createRole({
        name: formData.name.trim(),
        permissions:
          selectedPermissions.length > 0 ? selectedPermissions : undefined,
      });
      toast.success(translations.messages.success);
      onClose();
    } catch (_error) {
      toast.error(translations.messages.error);
    }
  }, [isFormValid, formData, createRole, toast, translations, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isFormValid && !isCreatePending) {
      handleSave();
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      className={cn('@AddRoleDialogBase', className)}
    >
      <DialogContent
        className="flex max-h-[80vh] min-w-[700px] flex-col"
        closeButton={false}
      >
        <div className="flex items-center justify-between px-4 pt-4">
          <DialogTitle className="text-xl font-bold">
            {translations.title}
          </DialogTitle>
          <button
            onClick={onClose}
            className="flex items-center justify-center rounded bg-neutral/5 p-[3px]"
            aria-label={translations.actions.cancel}
          >
            <LucideX className="size-6 text-neutral/60" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-4 py-5">
          <TextField
            aria-label={translations.fields.name.label}
            label={translations.fields.name.label}
            placeholder={translations.fields.name.placeholder}
            value={formData.name}
            onChange={onUpdateName}
            onKeyDown={handleKeyDown}
            isRequired
            autoFocus
          />
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <PermissionsEditor
            modules={permissionModules}
            value={formData.permissions}
            onChange={onPermissionChange}
            onBulkChange={onBulkPermissionChange}
          />
        </div>

        <DialogFooter>
          <Button
            aria-label={translations.actions.cancel}
            variant="outline"
            color="neutral"
            onPress={onClose}
            isDisabled={isCreatePending}
          >
            {translations.actions.cancel}
          </Button>
          <Button
            aria-label={translations.actions.save}
            variant="solid"
            color="primary"
            onPress={handleSave}
            isDisabled={!isFormValid || isCreatePending}
            isLoading={isCreatePending}
          >
            {translations.actions.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
